import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './CommandPalette.css';

const ACTIONS = [
  { key: 'billing', label: 'New bill', icon: '🧾', words: 'sale order invoice billing' },
  { key: 'purchases', label: 'Add purchase', icon: '🚚', words: 'buy stock supplier inward' },
  { key: 'expenses', label: 'Add expense', icon: '💸', words: 'spend cost petty cash' },
  { key: 'shifts', label: 'End shift & count stock', icon: '🔁', words: 'handover cash close shift' },
  { key: 'stockcount', label: 'Check stock', icon: '📦', words: 'inventory low available' },
  { key: 'billhistory', label: 'Find a bill', icon: '🔎', words: 'search reprint edit delete history' },
  { key: 'profit', label: "Today's profit", icon: '📈', words: 'report margin daily' },
];

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
const ctxNow = () => window.__icecreamPalette || null;

function flatten(nav) {
  const out = [];
  (nav || []).forEach((n) => {
    const kids = Object.values(n).find((v) => Array.isArray(v));
    if (kids) kids.forEach((c) => out.push({ ...c, group: n.label }));
    else out.push({ ...n, group: 'Main' });
  });
  return out;
}

function loadRecent() { try { return JSON.parse(localStorage.getItem('icecream_palette_recent') || '[]'); } catch (e) { return []; } }
function saveRecent(k) {
  try { localStorage.setItem('icecream_palette_recent', JSON.stringify([k, ...loadRecent().filter((x) => x !== k)].slice(0, 4))); } catch (e) {}
}

function Palette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const show = () => { if (ctxNow()) { setQ(''); setSel(0); setOpen(true); } };
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k') {
        if (!ctxNow()) return;
        e.preventDefault();
        setOpen((o) => { if (!o) { setQ(''); setSel(0); } return !o; });
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('icecream-palette', show);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('icecream-palette', show); };
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current && inputRef.current.focus(), 20); }, [open]);

  const ctx = open ? ctxNow() : null;

  const rows = useMemo(() => {
    if (!ctx) return [];
    const pages = flatten(ctx.nav);
    const byKey = {}; pages.forEach((p) => { byKey[p.key] = p; });
    const ok = (key) => {
      const p = byKey[key];
      if (!p) return false;
      if (key === 'dashboard' && !ctx.isOwner) return false;
      return !p.perm || !!(ctx.perms || {})[p.perm];
    };
    const pageRows = pages.filter((p) => ok(p.key)).map((p) => ({ id: 'p:' + p.key, key: p.key, label: p.label, hint: p.group, icon: '↗', section: 'Pages', words: '' }));
    const actRows = ACTIONS.filter((x) => ok(x.key)).map((x) => ({ id: 'a:' + x.label, key: x.key, label: x.label, hint: byKey[x.key].label, icon: x.icon, section: 'Quick actions', words: x.words }));
    const term = q.trim().toLowerCase();
    if (!term) {
      const recent = loadRecent().filter(ok).map((k) => ({ ...pageRows.find((r) => r.key === k), id: 'r:' + k, section: 'Recent', icon: '🕘' })).filter((r) => r.label);
      return [...recent, ...actRows, ...pageRows];
    }
    const toks = term.split(/\s+/);
    const score = (r) => {
      const hay = (r.label + ' ' + r.hint + ' ' + r.words).toLowerCase();
      if (!toks.every((t) => hay.includes(t))) return -1;
      const l = r.label.toLowerCase();
      return l.startsWith(term) ? 3 : l.includes(term) ? 2 : 1;
    };
    return [...actRows, ...pageRows].map((r) => ({ r, s: score(r) })).filter((x) => x.s > 0)
      .sort((x, y) => y.s - x.s).map((x) => ({ ...x.r, section: 'Results' }));
  }, [ctx, q]);

  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => {
    const el = listRef.current && listRef.current.querySelector(`[data-i="${sel}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  if (!open || !ctx) return null;

  const go = (r) => { if (!r) return; saveRecent(r.key); setOpen(false); ctx.go(r.key); };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(rows.length - 1, s + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(rows[sel]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
  };

  let last = '';
  return (
    <div className="cp-overlay" onMouseDown={() => setOpen(false)}>
      <div className="cp-box" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="Search">
        <div className="cp-search">
          <span className="cp-glass">⌕</span>
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Search pages and actions… (bill, stock, profit)" />
          <kbd>esc</kbd>
        </div>
        <div className="cp-list" ref={listRef}>
          {rows.length === 0 && <div className="cp-empty">Nothing found for “{q}”</div>}
          {rows.map((r, idx) => {
            const head = r.section !== last ? (last = r.section) : null;
            return (
              <React.Fragment key={r.id}>
                {head && <div className="cp-section">{head}</div>}
                <button type="button" data-i={idx} className={'cp-row' + (idx === sel ? ' is-sel' : '')}
                  onMouseMove={() => idx !== sel && setSel(idx)} onClick={() => go(r)}>
                  <span className="cp-icon">{r.icon}</span>
                  <span className="cp-label">{r.label}</span>
                  <span className="cp-hint">{r.hint}</span>
                  {idx === sel && <span className="cp-enter">↵</span>}
                </button>
              </React.Fragment>
            );
          })}
        </div>
        <div className="cp-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>{isMac ? '⌘' : 'Ctrl'}</kbd><kbd>K</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
}

export function mountCommandPalette() {
  if (document.getElementById('icecream-palette-root')) return;
  const el = document.createElement('div');
  el.id = 'icecream-palette-root';
  document.body.appendChild(el);
  createRoot(el).render(<Palette />);
}

export default Palette;
